import type { AppLanguage } from '@caro/shared/utils';

// Zelfde vier talen als de gedeelde taalketen (specs/20261016-taal-bewonersapp, T010).
export type Language = AppLanguage;

export const LANGUAGES: { code: Language; name: string }[] = [
  { code: 'nl', name: 'Nederlands' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Fran\u00e7ais' },
  { code: 'de', name: 'Deutsch' },
];

export const DEFAULT_LANGUAGE: Language = 'nl';

export interface Translations {
  // Common
  common: {
    back: string;
    cancel: string;
    save: string;
    delete: string;
    retry: string;
    loading: string;
    close: string;
    yes: string;
    no: string;
    ok: string;
    or: string;
    play: string;
    stop: string;
    goBack: string;
    returnHome: string;
    dismiss: string;
    /** Naam op de tablet van een placeholder-persoon (IM-22). */
    unoccupied: string;
    /** Algemene aanspreking als de naam uit staat (weergave-instelling showResidentName). */
    resident: string;
  };

  // Login
  login: {
    appName: string;
    subtitle: string;
    emailLabel: string;
    passwordLabel: string;
    signingIn: string;
    signIn: string;
    qrButton: string;
    helpText: string;
    adminSetup: string;
    activeSessionTitle: string;
    activeSessionMessage: string;
    logoutOthers: string;
    cancelLogin: string;
  };

  // Home
  home: {
    greetingMorning: string;
    greetingAfternoon: string;
    greetingEvening: string;
    greetingNight: string;
    phoneReady: string;
    phoneConnecting: string;
    phoneError: string;
    geofenceOutside: string;
    geofenceInside: string;
    geofenceIndicatorOutside: string;
    geofenceIndicatorSafe: string;
    /** T6.1 / D1 — aria-label for the visible "Edit layout" pencil button. */
    editLayoutEdit: string;
    /** T6.1 / D1 — aria-label for the same button while edit mode is active. */
    editLayoutDone: string;
    /** R5 / T050 — front-page open-door button label. */
    openDoor: string;
    /** R5 / T050 — success toast after the door-open request succeeds. */
    openDoorSuccess: string;
    /** R5 / T050 — failure toast after the door-open request fails. */
    openDoorError: string;
    /** Home "Apps/Menu" button → opens the app launcher (AppsPage). */
    apps: string;
    /** Name of the action-buttons widget, shown while editing the layout. The
     *  other widgets reuse buttonLabels.weather / today.title / buttonLabels.news. */
    widgetButtons: string;
  };

  // Emergency
  emergency: {
    confirmationTitle: string;
    buttonYes: string;
    buttonNo: string;
    buttonNoCancel: string;
    buttonCancel: string;
    countdownMessage: string;
    calling: string;
    connected: string;
    resetAlarm: string;
    cancelled: string;
    errorApiFailed: string;
    errorSipUnavailable: string;
    call112: string;
    tryAgain: string;
    callHelp: string;
    fiveSecondsRemaining: string;
    dispatchConnecting: string;
    dispatchRetry: string;        // contains {n}
    dispatchFailed: string;
    dispatchFailedVoice: string;
    tryingToCall: string;
    callingWith: string;
    onRoute: string;
    alarmSent: string;
    alarmActive: string;
  };

  // Settings
  settings: {
    title: string;
    homeScreen: string;
    customizeButtons: string;
    connectionStatus: string;
    allConnected: string;
    display: string;
    language: string;
    about: string;
    testMode: string;
    logout: string;
    replayOnboarding: string;
    permissions: string;
    permissionsValue: string;
    permissionsTitle: string;
    permissionsIntro: string;
    permissionsAllowAll: string;
    permissionsRecheck: string;
    permissionsAllGranted: string;
    permGranted: string;
    permDenied: string;
    permPrompt: string;
    permDeniedHelp: string;
    permMic: string;
    permMicDesc: string;
    permCamera: string;
    permCameraDesc: string;
    permLocation: string;
    permLocationDesc: string;
    permNotifications: string;
    permNotificationsDesc: string;
  };

  // Home-screen action button labels (translated client-side by target path;
  // custom/unknown buttons fall back to their stored label).
  buttonLabels: {
    dialer: string;
    contacts: string;
    photos: string;
    info: string;
    games: string;
    voicemail: string;
    callHistory: string;
    settings: string;
    openDoor: string;
    agenda: string;
    weather: string;
    news: string;
    energy: string;
  };

  // Interim audio-activation overlay (web only, until the APK ships)
  // Onboarding
  onboarding: {
    splashTagline: string;
    welcomeTitle: string;
    welcomeSubtitle: string;
    connectedTitle: string;
    connectedSubtitle: string;
    homeScreenTitle: string;
    homeScreenSubtitle: string;
    getStartedTitle: string;
    getStartedSubtitle: string;
    next: string;
    skip: string;
    getStartedButton: string;
  };

  // Contacts
  contacts: {
    title: string;
    emptyTitle: string;
    emptyDescription: string;
    callContact: string;
    loadError: string;
  };

  // Call History
  callHistory: {
    title: string;
    filterAll: string;
    filterMissed: string;
    filterAnswered: string;
    filterVoicemail: string;
    loadError: string;
    networkError: string;
    noCalls: string;
    today: string;
    yesterday: string;
    /** Per-call outcome badges. Distinct from the filter* labels above, which
     *  name the filter tabs rather than an individual call's result. */
    statusAnswered: string;
    statusMissed: string;
    statusVoicemail: string;
    statusRinging: string;
    statusRejected: string;
    statusFailed: string;
  };

  // Voicemail
  voicemail: {
    title: string;
    loadingVoicemails: string;
    loadError: string;
    emptyTitle: string;
    emptyDescription: string;
    statusKept: string;
    statusAutoDelete: string;
    keep: string;
    deleteVoicemail: string;
    confirmDelete: string;
    playError: string;
    deleteError: string;
    updateError: string;
    /** Relative day prefixes in the voicemail timestamp list. */
    today: string;
    yesterday: string;
    /** 2–6 days old. Placeholder: {n}. Never rendered with n < 2, so the
     *  plural form is safe. */
    daysAgo: string;
  };

  // In Call
  inCall: {
    connecting: string;
    dialing: string;
    onHold: string;
    ended: string;
    mute: string;
    unmute: string;
    keypad: string;
    speaker: string;
    earpiece: string;
    hold: string;
    resume: string;
  };

  // Incoming Call
  incomingCall: {
    emergencyCallback: string;
    incoming: string;
    autoAnswering: string;
    decline: string;
    answer: string;
  };

  // Doorbell
  doorbell: {
    viewing: string;
    calling: string;
    active: string;
    doorOpened: string;
    opening: string;
    done: string;
    noCamera: string;
    entrance: string;
    openDoor: string;
    answer: string;
    decline: string;
    hangup: string;
    // R4 — videophone two-button matrix labels
    afwijzen: string;
    opnemen: string;
    ophangen: string;
    deurOpenen: string;
    // Door release (idea #8). Four labels for the four visual states (LD-5);
    // `doorOpened` above is deliberately NOT reused — it claims a physical
    // outcome nothing on this path observes (LD-15).
    doorReleasing: string;
    doorRetried: string;
    doorSent: string;
    doorFailed: string;
  };

  // Photos
  photos: {
    title: string;
    tabAll: string;
    tabFavorites: string;
    tabHidden: string;
    emptyFavorites: string;
    emptyHidden: string;
    emptyAll: string;
    /** Shown in place of a photo that failed to load (screensaver + gallery). */
    unavailable: string;
  };

  // Agenda
  agenda: {
    title: string;
    today: string;
    allDay: string;
    spots: string;
    open: string;
    addedBy: string;
    emptyMessage: string;
    cancelSignup: string;
    join: string;
    previousWeek: string;
    nextWeek: string;
    /** Day header for the day after today, alongside `today`. */
    tomorrow: string;
    /** "+ Voeg toe" — a resident writing in their own agenda. */
    addEntry: string;
    /** Explicit marker on a resident-authored private entry. */
    privateMarker: string;
    /** The line under it, in the resident's own words. */
    privateExplain: string;
    newEntryTitle: string;
    entryTitleLabel: string;
    entryWhenLabel: string;
    entryDurationLabel: string;
    save: string;
    cancel: string;
    delete: string;
    /** Signup states served by the server (LD-7); the tablet renders, never decides. */
    signupOpen: string;
    signupClosed: string;
    signupFull: string;
    signupAlready: string;
    /**
     * O-4: withdrawing closes once the occurrence has STARTED. The server decides
     * (`canCancel`) and the button is then absent; this is the line shown in its place.
     */
    /** Opens the organiser's page in the IN-APP browser (kiosk mode). */
    moreInfo: string;
    withdrawClosed: string;
    withdrawFailed: string;
    /** LD-7: shown INSTEAD of a button when the apartment has no billing account. */
    noBillingAccount: string;
    waitlisted: string;
    /** Attendee selection on a multi-resident unit (LD-14). */
    whoIsComing: string;
    attendeeYou: string;
    /** Price confirmation, the ONLY place an amount is shown (spec §9). */
    confirmTitle: string;
    confirmTotal: string;
    confirmOnApartmentBill: string;
    confirmYes: string;
    confirmNo: string;
    signupFailed: string;
    priceChanged: string;
  };

  // Dialer
  dialer: {
    title: string;
    connectingMessage: string;
    notConnectedMessage: string;
  };

  // Info
  info: {
    title: string;
    myProfile: string;
    name: string;
    email: string;
    location: string;
    myBuilding: string;
    building: string;
    needHelp: string;
    helpText: string;
    about: string;
    appVersion: string;
    /** Kaart "aan wie/welk appartement hangt deze tablet". */
    linkedTo: string;
    resident: string;
    apartment: string;
    site: string;
    extension: string;
    notLinked: string;
  };

  // Connectivity
  connectivity: {
    title: string;
    allConnected: string;
    limited: string;
    offline: string;
    checking: string;
    emergencyAvailable: string;
    degradedMessage: string;
    offlineMessage: string;
    networkLabel: string;
    networkDescription: string;
    internetLabel: string;
    internetDescription: string;
    apiLabel: string;
    apiDescription: string;
    sipLabel: string;
    sipDescription: string;
    mqttLabel: string;
    mqttDescription: string;
    statusOk: string;
    statusDown: string;
    checkAgain: string;
    noNetwork: string;
    offlineEmergencyText: string;
  };

  // Home Screen Settings
  homeScreenSettings: {
    title: string;
    subtitle: string;
    buttonCount: string;
    resetToDefault: string;
  };

  // Games Page
  games: {
    title: string;
    subtitle: string;
    // Game names and descriptions
    memory: { name: string; desc: string };
    sudoku: { name: string; desc: string };
    wordSearch: { name: string; desc: string };
    trivia: { name: string; desc: string };
    game2048: { name: string; desc: string };
    solitaire: { name: string; desc: string };
    ticTacToe: { name: string; desc: string };
    hangman: { name: string; desc: string };
    colorMatch: { name: string; desc: string };
    chess: { name: string; desc: string };
    checkers: { name: string; desc: string };
    findDifferences: { name: string; desc: string };
    slidingPuzzle: { name: string; desc: string };
  };

  // Game Common
  gameCommon: {
    playAgain: string;
    changeDifficulty: string;
    backToGames: string;
    newGame: string;
    tryAgain: string;
    keepPlaying: string;
    congratulations: string;
    score: string;
    best: string;
    time: string;
    moves: string;
    easy: string;
    medium: string;
    hard: string;
    gameOver: string;
  };

  // Memory Game
  memoryGame: {
    chooseDifficulty: string;
    matchPairsSubtitle: string;
    encourageAmazing: string;
    encourageGreat: string;
    encourageNice: string;
    encourageWellDone: string;
  };

  // Sudoku
  sudokuGame: {
    chooseDifficulty: string;
    blanks: string;
    hintsUsed: string;
    hintsDisplay: string;
    solvedPuzzle: string;
  };

  // 2048
  game2048: {
    youReached: string;
  };

  // Trivia
  triviaGame: {
    chooseCategory: string;
    questions: string;
    questionProgress: string;
    correct: string;
    incorrect: string;
    nextQuestion: string;
    seeResults: string;
    chooseCategoryBtn: string;
    resultPerfect: string;
    resultExcellent: string;
    resultGreat: string;
    resultGood: string;
    resultNiceTry: string;
    // Category names
    history: string;
    nature: string;
    music: string;
    geography: string;
    science: string;
  };

  // Word Search
  wordSearchGame: {
    chooseTheme: string;
    words: string;
    theme: string;
    wordsFound: string;
    hintSelectStart: string;
    hintSelectEnd: string;
    allWordsFound: string;
    completionMessage: string;
    newPuzzle: string;
    changeTheme: string;
    // Theme names
    animals: string;
    food: string;
    natureTheme: string;
    home: string;
    colors: string;
  };

  // Hangman
  hangmanGame: {
    guessTheWord: string;
    category: string;
    youWon: string;
    youLost: string;
    theWordWas: string;
    wrongGuesses: string;
    // Categories
    animals: string;
    food: string;
    countries: string;
    household: string;
  };

  // Color Match
  colorMatchGame: {
    watchPattern: string;
    yourTurn: string;
    round: string;
    bestStreak: string;
    wrongPattern: string;
    newRecord: string;
  };

  // Solitaire
  solitaireGame: {
    undo: string;
    draw: string;
    autoComplete: string;
    youWon: string;
    movesUsed: string;
  };

  // Tic Tac Toe
  ticTacToeGame: {
    yourTurn: string;
    aiTurn: string;
    youWin: string;
    aiWins: string;
    draw: string;
    chooseDifficulty: string;
    wins: string;
    losses: string;
    draws: string;
  };

  // In-App Browser
  browser: {
    title: string;
    noUrl: string;
    emergency: string;
  };

  // Display & Theme Settings
  display: {
    title: string;
    brightness: string;
    screen: string;
    sleepMode: string;
    sleepModeDesc: string;
    tapToWake: string;
    fontSize: string;
    fontNormal: string;
    fontLarge: string;
    fontXLarge: string;
    fontHuge: string;
    fontGiant: string;
    colorTheme: string;
    light: string;
    dark: string;
    highContrast: string;
    screensaverMode: string;
    screensaverPhotos: string;
    screensaverClockOnly: string;
    standbyBackground: string;
    clockStyle: string;
    clockDigital: string;
    clockAnalog: string;
    clockSize: string;
    clockSmall: string;
    clockMedium: string;
    clockLarge: string;
    clockXLarge: string;
    clockHuge: string;
    clockBold: string;
    clockBoldOff: string;
    clockBoldOn: string;
    clockShowYear: string;
    clockShowYearOff: string;
    clockShowYearOn: string;
    weatherLocation: string;
    weatherLocationPlaceholder: string;
    weatherLocationHint: string;
    detectLocation: string;
    /** T6.5 / D6 — section heading for the reduce-motion toggle row. */
    reduceMotion: string;
    /** T6.5 / D6 — helper text shown beneath the reduce-motion toggle. */
    reduceMotionHint: string;
    /** T6.5 / D6 — three-state options for the reduce-motion control. */
    reduceMotionAuto: string;
    reduceMotionOn: string;
    reduceMotionOff: string;
  };

  // Today
  today: {
    title: string;
    noEvents: string;
    now: string;
    allDay: string;
    inTime: string;
    /** Screensaver next-event line. Placeholders: {title}, {time}. */
    nextEvent: string;
  };

  // Energie (spec 20261013-metering-afrekening)
  energy: {
    title: string;
    loading: string;
    unavailable: string;
    noMeter: string;
    thisMonth: string;
    lastYearSameMonth: string;
    sinceJanuary: string;
    /** Placeholder {value}. */
    middleApartmentSoFar: string;
    last12: string;
    showList: string;
    showChart: string;
    running: string;
    thisYear: string;
    lastYear: string;
    /** Placeholders {month}, {you}, {middle}. */
    comparison: string;
    comparisonNote: string;
    /** Placeholders {date}, {value}. */
    lastSettlement: string;
    footer: string;
  };

  // Weather
  weather: {
    title: string;
    feelsLike: string;
    humidity: string;
    wind: string;
    sunrise: string;
    sunset: string;
    forecast: string;
    loading: string;
    unavailable: string;
    high: string;
    low: string;
  };

  // News
  news: {
    title: string;
    loading: string;
    noArticles: string;
    readMore: string;
    sources: string;
    minutesAgo: string;
    hoursAgo: string;
  };

  // Permissions
  permissions: {
    title: string;
    description: string;
    microphone: string;
    microphoneDesc: string;
    camera: string;
    cameraDesc: string;
    location: string;
    locationDesc: string;
    allowAll: string;
    deniedHelp: string;
    notifications: string;
    notificationsDesc: string;
    openSettings: string;
  };

  // Battery
  battery: {
    lowTitle: string;
    lowMessage: string;
    highTitle: string;
    highMessage: string;
    lowSpoken: string;
  };

  // ── specs/20261016-taal-bewonersapp (T011, FR-4) ─────────────────────────
  // Placeholders tussen accolades ({name}, {n}, …) vult `tf()` uit `i18n/format.ts` in.

  // Domotica. De NAMEN van uitgangen, sferen en ruimtes zijn gegevens van de bewoner en
  // worden niet vertaald; alleen de woorden eromheen.
  domotics: {
    title: string;
    back: string;
    backPlain: string;
    loading: string;
    notReady: string;
    notEnabled: string;
    editTitle: string;
    editButton: string;
    doneButton: string;
    done: string;
    editModeHint: string;
    allOff: string;
    oneOutputOn: string;
    manyOutputsOn: string;
    rooms: string;
    all: string;
    noRoomsYet: string;
    /** Placeholder {label}. */
    roomsHint: string;
    nothingConfigured: string;
    newScene: string;
    newSceneAria: string;
    /** De standaardruimte van sferen zonder eigen ruimte. */
    scenesFolder: string;
    errorRenameImmutable: string;
    errorSaveFailed: string;
    errorSceneLimit: string;
    errorSceneEmpty: string;
    errorSceneNameRequired: string;
    errorSceneImmutable: string;
    errorDeleteFailed: string;
    errorSceneOpenFailed: string;
    errorHomeFailed: string;
    errorPrefFailed: string;
    errorRoomFailed: string;
    notSent: string;
    retry: string;
    sensorActive: string;
    sensorIdle: string;
    sending: string;
    /** Placeholder {name}. */
    editAria: string;
    /** Placeholders {name}, {extra}. */
    readOnlyAria: string;
    longPressHint: string;
    hiddenBadge: string;
    editBadge: string;
    /** Placeholder {name}. */
    strengthOf: string;
    close: string;
    weaker: string;
    stronger: string;
    strength: string;
    off: string;
    full: string;
    editScene: string;
    editSceneSub: string;
    rename: string;
    /** Placeholder {name}. */
    renameSub: string;
    moveToRoom: string;
    /** Placeholder {room}. */
    inRoom: string;
    inNoRoom: string;
    homeRemove: string;
    homeAdd: string;
    homeRemoveSub: string;
    homeAddSub: string;
    show: string;
    hide: string;
    showSub: string;
    hideSub: string;
    templateNote: string;
    /** Placeholder {name}. */
    whereTitle: string;
    cancel: string;
    currentRoom: string;
    /** Placeholder {room}. */
    removeFromRoom: string;
    removeFromRoomSub: string;
    newRoom: string;
    newRoomPlaceholder: string;
    create: string;
    renameQuestion: string;
    save: string;
    saving: string;
    newSceneTitle: string;
    confirmDelete: string;
    delete: string;
    sceneName: string;
    sceneNamePlaceholder: string;
    sceneRoomQuestion: string;
    sceneRoomPlaceholder: string;
    sceneWhichLamps: string;
    sceneCaptureNow: string;
    sceneNoLamps: string;
    /** Placeholder {name}. */
    sceneStrengthOf: string;
    on: string;
    offLower: string;
  };

  // Meldingen en berichten (inbox, dialoog, volledig scherm, actieknoppen, achtergrond).
  notifications: {
    title: string;
    backToHome: string;
    empty: string;
    priorityUrgent: string;
    priorityHigh: string;
    priorityNormal: string;
    priorityLow: string;
    read: string;
    unread: string;
    /** Placeholder {time}. */
    today: string;
    close: string;
    stayUntilClosed: string;
    /** Placeholder {n}. */
    closesIn: string;
    pausedDuringCall: string;
    busy: string;
    errorRouteNotAllowed: string;
    errorNotDelivered: string;
    errorUnavailable: string;
    errorOffline: string;
    errorExpired: string;
    errorGeneric: string;
    /** Placeholders {n}, {total}. */
    backgroundOf: string;
    cameraUnavailable: string;
    imageUnavailable: string;
    previousImage: string;
    nextImage: string;
    /** Placeholder {n}. */
    goToImage: string;
    /** Systeemmeldingen met een sleutel in plaats van tekst (FR-19, T035b). */
    system: {
      system_test: { title: string; body: string };
    };
  };

  // Deurbel met camera's.
  doorbellCamera: {
    door: string;
    /** Placeholder {name}. */
    camera: string;
    /** Placeholder {time}. */
    countdown: string;
  };

  // Titels van de pop-ups die een opdracht van het portaal toont (useMqttCommands).
  commandPopup: {
    message: string;
    speaking: string;
    playingSound: string;
    /** Placeholder {sound}. */
    sound: string;
    /** Placeholder {n}. */
    times: string;
  };

  // Schermlezerteksten van de spelletjes.
  gamesA11y: {
    goBack: string;
    backToGames: string;
    moveUp: string;
    moveLeft: string;
    moveDown: string;
    moveRight: string;
    flipCard: string;
    faceDownCard: string;
    emptySlot: string;
    /** Placeholders {rank}, {suit}. */
    cardOf: string;
    /** Placeholder {n}. */
    cell: string;
    empty: string;
    /** Placeholders {row}, {col}. */
    rowCol: string;
    /** Placeholder {value}. */
    valueOf: string;
    given: string;
    redPiece: string;
    blackPiece: string;
    white: string;
    black: string;
    hint: string;
    /** Placeholder {n}. */
    tile: string;
    tapToSlide: string;
    /** Placeholders {wrong}, {max}. */
    hangmanFigure: string;
  };
}
